import React, { useEffect, useState, useRef, useCallback } from "react";
import moment from "moment";
import { fetchData } from "../../helpers/externapi";
import CommonTables from "../../Commoncomponents/CommonTables";

// How long (ms) to wait before marking a PENDING (newly triggered) report as Failed
// Only applies to the report triggered from Schedule Report button
const PENDING_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
// How often to poll (ms)
const POLL_INTERVAL_MS = 4000;

export default function ReportsData() {
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // The ReportsId we are actively waiting to appear/complete (from localStorage)
    const [pendingReportId, setPendingReportId] = useState(null);
    // true while we are still waiting for the pending report to finish
    const [waitingForReport, setWaitingForReport] = useState(false);
    // true once everything is done (all completed or failed)
    const [allSettled, setAllSettled] = useState(false);
    // Only the PENDING report can be locally failed (timeout). Key = ReportsId string.
    const [localFailedIds, setLocalFailedIds] = useState(new Set());

    const intervalRef = useRef(null);
    // Refs so interval callbacks always have latest values without re-creating interval
    const pendingReportIdRef = useRef(null);
    const scheduledAtRef = useRef(null);
    const localFailedIdsRef = useRef(new Set());

    let UserId = localStorage.getItem("UserId");

    // ─── Sync localFailedIds to ref ───────────────────────────────────────────
    useEffect(() => {
        localFailedIdsRef.current = localFailedIds;
    }, [localFailedIds]);

    // ─── Status helpers ───────────────────────────────────────────────────────
    const isActiveStatus = (status) =>
        ["Initiated", "Processing", "In Progress"].includes(status);

    const getStatusStyles = (status) => {
        switch (status) {
            case "Completed": return { bg: "#d4f8d4", color: "#28a745" };
            case "Initiated": return { bg: "#cce5ff", color: "#007bff" };
            case "Processing": return { bg: "#ffeeba", color: "#fd7e14" };
            case "In Progress": return { bg: "#fff3cd", color: "#ffc107" };
            case "Failed": return { bg: "#f8d7da", color: "#dc3545" };
            default: return { bg: "#f8d7da", color: "#dc3545" };
        }
    };

    // ─── Read pending report from localStorage on mount ──────────────────────
    useEffect(() => {
        const storedId = localStorage.getItem("pendingReportId");
        const storedAt = localStorage.getItem("pendingReportScheduledAt");
        if (storedId) {
            setPendingReportId(storedId);
            pendingReportIdRef.current = storedId;
            setWaitingForReport(true);
            scheduledAtRef.current = storedAt ? Number(storedAt) : Date.now();
        }
    }, []);

    // ─── Clear pending state once report is done ─────────────────────────────
    const clearPendingReport = useCallback(() => {
        localStorage.removeItem("pendingReportId");
        localStorage.removeItem("pendingReportScheduledAt");
        setPendingReportId(null);
        pendingReportIdRef.current = null;
        setWaitingForReport(false);
        scheduledAtRef.current = null;
    }, []);

    // ─── Core fetch function ──────────────────────────────────────────────────
    const getReportHistory = useCallback(
        async (showLoader = false) => {
            if (showLoader) setLoading(true);

            try {
                const response = await fetchData(
                    `lambdaAPI/Reports/ReportsHistoryByUserId`,
                    {
                        skip: (currentPage - 1) * perPage,
                        take: perPage,
                        UserId,
                    }
                );

                const sorted = Array.isArray(response)
                    ? [...response].sort(
                        (a, b) => new Date(b.StartTime) - new Date(a.StartTime)
                    )
                    : [];

                setFilteredData(sorted);

                const now = Date.now();
                const pendingId = pendingReportIdRef.current;

                // ── ONLY check timeout for the PENDING report, not all active reports ──
                if (pendingId) {
                    const pendingEntry = sorted.find(
                        (r) => String(r.ReportsId) === String(pendingId)
                    );

                    const alreadyFailed = localFailedIdsRef.current.has(pendingId);

                    if (!alreadyFailed) {
                        if (pendingEntry) {
                            // Report appeared in history
                            if (pendingEntry.Status === "Completed") {
                                // Done successfully
                                clearPendingReport();
                            } else if (isActiveStatus(pendingEntry.Status)) {
                                // Still processing — check if it has been running too long
                                // Use scheduledAt as the start reference (most accurate)
                                const startedAt = scheduledAtRef.current || now;
                                if (now - startedAt > PENDING_TIMEOUT_MS) {
                                    setLocalFailedIds((prev) => new Set([...prev, pendingId]));
                                    localFailedIdsRef.current = new Set([
                                        ...localFailedIdsRef.current,
                                        pendingId,
                                    ]);
                                    clearPendingReport();
                                }
                            }
                            // If status is already "Failed" from server side, just clear pending
                            else if (pendingEntry.Status === "Failed") {
                                clearPendingReport();
                            }
                        } else {
                            // Report hasn't appeared in history yet — check appearance timeout
                            const startedAt = scheduledAtRef.current || now;
                            if (now - startedAt > PENDING_TIMEOUT_MS) {
                                // Timed out waiting to even appear
                                setLocalFailedIds((prev) => new Set([...prev, pendingId]));
                                localFailedIdsRef.current = new Set([
                                    ...localFailedIdsRef.current,
                                    pendingId,
                                ]);
                                clearPendingReport();
                            }
                        }
                    }
                }

                // ── Check if ALL rows are settled ──
                // A row is settled if: server says Completed/Failed, OR it's in localFailedIds
                const currentFailed = localFailedIdsRef.current;
                const allDone =
                    sorted.length > 0 &&
                    sorted.every(
                        (r) =>
                            r.Status === "Completed" ||
                            r.Status === "Failed" ||
                            currentFailed.has(String(r.ReportsId))
                    );

                setAllSettled(allDone);

                if (allDone && intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            } catch (error) {
                console.error("Error fetching report history:", error);
                setFilteredData([]);
            } finally {
                setLoading(false);
            }
        },
        [currentPage, perPage, UserId, clearPendingReport]
    );

    const getReportsHistoryCount = useCallback(async () => {
        try {
            const res = await fetchData(`CommonRowCount/GetTableRowCount`, {
                tableName: "ReportsHistory",
                UserId,
            });
            setTotalCount(res[0]?.ReportsHistory || res[0]?.CountOfRecords || 0);
        } catch (err) {
            console.error("Error fetching count:", err);
        }
    }, [UserId]);

    // ─── Refresh handler (manual) ─────────────────────────────────────────────
    const handleRefresh = async () => {
        setRefreshing(true);
        await getReportHistory(false);
        await getReportsHistoryCount();
        setRefreshing(false);
    };

    // ─── Initial load on mount / page change ─────────────────────────────────
    useEffect(() => {
        getReportHistory(true);
        getReportsHistoryCount();
    }, [currentPage, perPage]);

    // ─── Auto-poll: runs as long as not allSettled ────────────────────────────
    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!allSettled) {
            intervalRef.current = setInterval(() => {
                getReportHistory(false);
            }, POLL_INTERVAL_MS);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [allSettled, getReportHistory]);

    // ─── Build table rows ─────────────────────────────────────────────────────
    const tableHeads = ["Report Name", "Start Time", "End Time", "Status", "Action"];

    const tableElements =
        filteredData.length > 0
            ? filteredData.map((report) => {
                const reportIdStr = String(report.ReportsId);
                // Only mark as locally failed if it's in our localFailedIds set
                const isLocallyFailed = localFailedIds.has(reportIdStr);
                // Server-side failed OR locally timed out
                const isFailed = isLocallyFailed || report.Status === "Failed";
                const effectiveStatus = isFailed ? "Failed" : report.Status;
                const statusStyle = getStatusStyles(effectiveStatus);
                const isActive = isActiveStatus(report.Status) && !isFailed;
                const isPending =
                    reportIdStr === String(pendingReportId) && waitingForReport;

                return [
                    // ── Report Name ──
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {isPending && (
                            <span
                                style={{
                                    display: "inline-block",
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: "#007bff",
                                    animation: "pulse-dot 1.4s ease-in-out infinite",
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <span>{report.ReportName}</span>
                    </div>,

                    // ── Start Time ──
                    report.StartTime
                        ? moment(report.StartTime).format("YYYY-MMM-DD hh:mm A")
                        : isPending
                            ? (
                                <span className="text-muted" style={{ fontSize: "12px" }}>
                                    Queued…
                                </span>
                            )
                            : "-",

                    // ── End Time ──
                    report.EndTime
                        ? moment(report.EndTime).format("YYYY-MMM-DD hh:mm A")
                        : isActive
                            ? (
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ gap: "6px" }}
                                >
                                    <div
                                        className="spinner-border spinner-border-sm text-primary"
                                        role="status"
                                        style={{ width: "14px", height: "14px" }}
                                    />
                                    <span
                                        className="text-primary fw-semibold"
                                        style={{ fontSize: "12px" }}
                                    >
                                        Processing…
                                    </span>
                                </div>
                            )
                            : "-",

                    // ── Status Badge ──
                    <span
                        style={{
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            padding: "5px 8px",
                            borderRadius: "5px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            textTransform: "uppercase",
                            fontSize: "10px",
                            fontWeight: "600",
                            minWidth: "95px",
                            justifyContent: "center",
                        }}
                    >
                        {isActive && (
                            <span
                                style={{
                                    width: "7px",
                                    height: "7px",
                                    borderRadius: "50%",
                                    backgroundColor: statusStyle.color,
                                    animation: "pulse-dot 1.4s ease-in-out infinite",
                                    display: "inline-block",
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        {effectiveStatus}
                    </span>,

                    // ── Action ──
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        {effectiveStatus === "Completed" ? (
                            <a
                                href={report.ReportsLink}
                                download
                                style={{
                                    backgroundColor: "#007bff",
                                    color: "#fff",
                                    border: "none",
                                    padding: "6px 14px",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    textDecoration: "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                }}
                            >
                                <i className="bi bi-download" />
                                Download
                            </a>
                        ) : isFailed ? (
                            <span
                                style={{
                                    color: "#dc3545",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                }}
                            >
                                <i className="bi bi-x-circle" />
                                Report Failed
                            </span>
                        ) : (
                            <div
                                className="d-flex align-items-center"
                                style={{
                                    color: "#fd7e14",
                                    fontWeight: "600",
                                    gap: "6px",
                                    fontSize: "12px",
                                }}
                            >
                                <div
                                    className="spinner-grow spinner-grow-sm"
                                    role="status"
                                    style={{ width: "12px", height: "12px" }}
                                />
                                Processing…
                            </div>
                        )}
                    </div>,
                ];
            })
            : [];

    // ─── Header status banner ─────────────────────────────────────────────────
    const renderStatusBanner = () => {
        if (loading) return <span />;

        if (allSettled) {
            return (
                <span className="text-success fw-semibold" style={{ fontSize: "13px" }}>
                    <i className="bi bi-check-circle-fill me-1" />
                    All reports completed
                </span>
            );
        }

        if (waitingForReport) {
            return (
                <span
                    className="d-flex align-items-center fw-semibold"
                    style={{ gap: "8px", color: "#007bff", fontSize: "13px" }}
                >
                    <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ width: "15px", height: "15px" }}
                    />
                    Generating report — this may take a moment…
                </span>
            );
        }

        const hasActive = filteredData.some(
            (r) =>
                isActiveStatus(r.Status) &&
                !localFailedIds.has(String(r.ReportsId)) &&
                r.Status !== "Failed"
        );

        if (hasActive) {
            return (
                <span
                    className="d-flex align-items-center fw-semibold"
                    style={{ gap: "8px", color: "#fd7e14", fontSize: "13px" }}
                >
                    <div className="spinner-grow spinner-grow-sm" role="status" />
                    Reports are processing…
                </span>
            );
        }

        return <span />;
    };

    const handlePageChange = (_, page) => setCurrentPage(page);
    const handlePerPageChange = (e) => {
        setPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.75); }
        }
      `}</style>

            <div className="card shadow-sm">
                {/* ── Header ── */}
                <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ paddingTop: "10px", paddingBottom: "10px", minHeight: "50px" }}
                >
                    {renderStatusBanner()}

                    <button
                        onClick={handleRefresh}
                        disabled={refreshing || loading}
                        className="btn btn-light btn-sm d-flex align-items-center"
                        style={{ gap: "5px" }}
                    >
                        <i
                            className="bi bi-arrow-clockwise"
                            style={{
                                display: "inline-block",
                                animation: refreshing ? "spin 0.8s linear infinite" : "none",
                            }}
                        />
                        {refreshing ? "Refreshing…" : "Refresh"}
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="card-body" style={{ margin: 0, padding: 0 }}>
                    {loading ? (
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "250px", flexDirection: "column", gap: "10px" }}
                        >
                            <div className="spinner-border text-primary" role="status" />
                            <span className="fw-semibold text-muted">Loading reports…</span>
                        </div>
                    ) : filteredData.length > 0 ? (
                        <CommonTables
                            tableHeads={tableHeads}
                            tableData={tableElements}
                            perPage={perPage}
                            currentPage={currentPage}
                            perPageChange={handlePerPageChange}
                            pageChange={handlePageChange}
                            totalCount={totalCount}
                        />
                    ) : waitingForReport ? (
                        // Pending report not yet in history
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ height: "200px", flexDirection: "column", gap: "12px" }}
                        >
                            <div className="spinner-border text-primary" role="status" />
                            <span className="fw-semibold text-muted">
                                Waiting for report to appear…
                            </span>
                            <span style={{ fontSize: "12px", color: "#888" }}>
                                We'll update automatically once it's ready
                            </span>
                        </div>
                    ) : (
                        <div className="text-center text-muted" style={{ padding: "30px" }}>
                            No reports found.
                        </div>
                    )}
                </div>
            </div>

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
        </>
    );
}